import { useState } from "react";
import { CalendarClock } from "lucide-react";
import { toast } from "sonner";

const MON = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const p2 = (n) => String(n).padStart(2, "0");
const yr = (y) => (y.length === 2 ? 2000 + Number(y) : Number(y));
const ok = (y, m, d) => y >= 1990 && y <= 2100 && m >= 1 && m <= 12 && (!d || (d >= 1 && d <= 31));
const out = (y, m, d) => (ok(y, m, d) ? `${y}-${p2(m)}${d ? `-${p2(d)}` : ""}` : null);

export function normDate(raw) {
  const s = String(raw || "").trim().toLowerCase();
  let m;
  if ((m = s.match(/^(\d{4})[-/.](\d{1,2})(?:[-/.](\d{1,2}))?/))) return out(+m[1], +m[2], m[3] && +m[3]);
  if ((m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/))) return out(yr(m[3]), +m[1], +m[2]);
  if ((m = s.match(/^(\d{1,2})[/.-](\d{4})$/))) return out(+m[2], +m[1]);
  if ((m = s.match(/^([a-z]{3})[a-z]*[\s,-]+(\d{1,2}[\s,]+)?(\d{2}|\d{4})$/)) && MON.includes(m[1])) return out(yr(m[3]), MON.indexOf(m[1]) + 1, m[2] && parseInt(m[2], 10));
  if ((m = s.match(/^\d{5}$/))) { const d = new Date(Date.UTC(1899, 11, 30) + Number(s) * 864e5); return out(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate()); }
  return null;
}

export function matchLeaseEnds(text, units) {
  const idx = new Map(units.map((u, i) => [String(u.unit).trim().toLowerCase(), i]));
  const rows = text.split(/\r?\n/).map((l) => (l.includes("\t") ? l.split("\t") : (l.trim().match(/^(\S+?)[,;\s]+(.+)$/) || [null, l]).slice(1)).map((c) => (c || "").trim())).filter((r) => r.some(Boolean));
  const body = rows.length && !normDate(rows[0][1]) && /unit|suite|apt|lease|end|expir/i.test(rows[0].join(" ")) ? rows.slice(1) : rows;
  const seen = new Set();
  return body.map(([unit = "", date = ""]) => {
    const i = idx.get(unit.toLowerCase());
    const d = normDate(date);
    const status = i === undefined ? "No such unit" : !d ? "Invalid date" : seen.has(i) ? "Duplicate" : "Matched";
    if (status === "Matched") seen.add(i);
    return { unit, raw: date, date: d, i, status, before: i === undefined ? null : units[i].lease_end || "" };
  });
}

const TONE = { Matched: "text-emerald-700", "No such unit": "text-amber-700", "Invalid date": "text-red-600", Duplicate: "text-amber-700" };

export function LeaseEndPaste({ id, units, setUnits, onClose }) {
  const [text, setText] = useState("");
  const rows = text.trim() ? matchLeaseEnds(text, units) : [];
  const good = rows.filter((r) => r.status === "Matched");
  const apply = () => {
    const m = new Map(good.map((r) => [r.i, r.date]));
    setUnits(units.map((u, i) => (m.has(i) ? { ...u, lease_end: m.get(i) } : u)));
    toast.success(`Lease end set on ${good.length} unit(s)${rows.length - good.length ? ` - ${rows.length - good.length} row(s) skipped` : ""}. Review, then save the rent roll.`);
    onClose();
  };
  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-3 space-y-2" data-testid={`${id}-lease-paste-box`}>
      <div className="text-[11px] text-slate-600 flex gap-1.5"><CalendarClock className="w-3.5 h-3.5 shrink-0 mt-0.5" />Paste two columns from Excel - unit number and lease end (header optional). Dates like 2027-06, 06/30/2027, Jun 2027 or Excel date numbers are understood. Nothing changes until you apply.</div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid={`${id}-lease-paste-input`} placeholder={"Unit\tLease end\n101\t06/30/2027\n102\t2027-09"} className="w-full text-xs num rounded-lg border border-slate-200 p-2 bg-white" />
      {rows.length > 0 && <>
        <div className="text-[11px] flex flex-wrap gap-x-4" data-testid={`${id}-lease-paste-counts`}>
          {["Matched", "No such unit", "Invalid date", "Duplicate"].map((k) => { const n = rows.filter((r) => r.status === k).length; return n ? <span key={k} className={TONE[k]}>{n} {k.toLowerCase()}</span> : null; })}
        </div>
        <div className="max-h-48 overflow-y-auto thin-scroll bg-white rounded-lg border border-slate-200">
          <table className="w-full text-xs" data-testid={`${id}-lease-paste-preview`}><thead className="sticky top-0 bg-slate-50"><tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">{["Unit", "Pasted", "Lease end now", "Will become", ""].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
            <tbody>{rows.map((r, n) => <tr key={n} className="border-t border-slate-100" data-testid={`${id}-lease-paste-row-${n}`}><td className="px-2 py-1 font-medium">{r.unit || "-"}</td><td className="px-2 num">{r.raw || "-"}</td><td className="px-2 num text-slate-500">{r.before ?? "-"}</td><td className="px-2 num">{r.status === "Matched" ? r.date : "-"}</td><td className={`px-2 ${TONE[r.status]}`}>{r.status}</td></tr>)}</tbody></table>
        </div>
      </>}
      <div className="flex gap-2">
        <button type="button" disabled={!good.length} onClick={apply} data-testid={`${id}-lease-paste-apply`} className="text-xs rounded-lg bg-slate-900 text-white px-3 py-1.5 disabled:opacity-40">Apply {good.length || ""} lease end{good.length === 1 ? "" : "s"}</button>
        <button type="button" onClick={onClose} data-testid={`${id}-lease-paste-cancel`} className="text-xs rounded-lg border border-slate-200 bg-white px-3 py-1.5">Cancel</button>
      </div>
    </div>
  );
}
